import { isApplicationSourceRevoked } from "@client/offline/artifactQueryPersistence";
import { APPLICATION_AVAILABILITY_QUERY_OPTIONS } from "@client/components/applications/applicationAvailability";
import { trpc } from "@client/trpc";
import { Text, Button, Skeleton } from "@sfc/ui";
import { Link } from "wouter";
import { HomeSection } from "./HomeSection";

const emptyApplications = (
  <HomeSection title="Your Applications">
    <Text variant="body-md" tone="muted-foreground">
      You haven’t started an application yet.
    </Text>
    <Link
      href="/apply"
      className="body-md text-primary underline underline-offset-4"
    >
      Explore funding rounds →
    </Link>
  </HomeSection>
);

export function HomeApplications({
  viewerId,
  enabled,
  showEmpty = false,
}: {
  viewerId: string;
  enabled: boolean;
  showEmpty?: boolean;
}) {
  if (!enabled) {
    return showEmpty ? (
      <HomeSection title="Your workspace">
        <Text variant="body-md" tone="muted-foreground">
          Your funding rounds will appear here when you join one.
        </Text>
      </HomeSection>
    ) : null;
  }

  return (
    <ApplicationList key={viewerId} viewerId={viewerId} showEmpty={showEmpty} />
  );
}

function ApplicationList({
  viewerId,
  showEmpty,
}: {
  viewerId: string;
  showEmpty: boolean;
}) {
  const query = trpc.getMyApplications.useQuery(undefined, {
    ...APPLICATION_AVAILABILITY_QUERY_OPTIONS,
    gcTime: 0,
  });
  const applications =
    !isApplicationSourceRevoked(query.error) &&
    query.data?.viewerId === viewerId
      ? query.data.applications
      : undefined;

  if (applications?.length === 0) {
    return showEmpty ? emptyApplications : null;
  }

  return (
    <HomeSection title="Your Applications">
      <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-surface-base">
        {query.isError && applications === undefined ? (
          <div role="alert" className="p-6">
            <p>Couldn’t load your applications.</p>
            <Button variant="nav" onClick={() => void query.refetch()}>
              Try again
            </Button>
          </div>
        ) : applications === undefined ? (
          <Skeleton
            className="h-28 w-full"
            role="status"
            aria-label="Loading applications"
          />
        ) : (
          applications.map((application) => (
            <article
              key={application.id}
              className="flex flex-wrap items-center gap-6 p-4 sm:p-6"
            >
              <div className="min-w-0 flex-1 basis-64">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <h3 className="heading-sm [overflow-wrap:anywhere]">
                    {application.roundName}
                  </h3>
                </div>
                <div className="mt-2 rounded-lg bg-muted/40 px-4 py-3 body-sm font-medium text-foreground">
                  {application.status === "SUBMITTED"
                    ? "Application Submitted"
                    : application.canOpen
                      ? "Application Not Yet Submitted"
                      : "Applications Closed"}
                </div>
                <Text
                  variant="body-md"
                  tone="muted-foreground"
                  className="mt-2"
                >
                  Team:{" "}
                  <Link
                    className="underline underline-offset-4"
                    href={`/teams/${application.team.id}`}
                  >
                    {application.team.name}
                  </Link>
                </Text>
              </div>
              <Button
                variant={
                  application.status !== "SUBMITTED" && application.canOpen
                    ? "default"
                    : "link"
                }
                className="min-h-11 max-w-full whitespace-normal"
                nativeButton={false}
                render={
                  <Link
                    href={`/applications/${encodeURIComponent(application.id)}${application.status !== "SUBMITTED" && application.canOpen ? "?edit=1" : ""}`}
                  />
                }
              >
                {application.status === "SUBMITTED" || !application.canOpen
                  ? "View application →"
                  : "Continue application →"}
              </Button>
            </article>
          ))
        )}
      </div>
    </HomeSection>
  );
}
